import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    List, ListItem, ListItemButton, ListItemText, Collapse, Button, CircularProgress, Box, Typography
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

// START OF EDIT: Symmetrical open/close animation variants
const mobileNavVariants = {
    hidden: {
        height: "0vh",
        opacity: 1,
        borderBottomLeftRadius: "50%",
        borderBottomRightRadius: "50%",
        transition: {
            type: "tween",
            ease: "easeInOut",
            duration: 0.2, // Faster close
            when: "afterChildren",
            staggerChildren: 0.05,
            staggerDirection: -1
        }
    },
    visible: {
        height: "100vh",
        opacity: 1,
        borderBottomLeftRadius: "0%",
        borderBottomRightRadius: "0%",
        transition: {
            type: "tween",
            ease: "easeInOut",
            duration: 0.2, // Faster open
            when: "beforeChildren",
            staggerChildren: 0.05
        }
    }
};
// END OF EDIT

const itemVariants = {
    visible: { y: 0, opacity: 1, transition: { y: { stiffness: 1000, velocity: -100 } } },
    hidden: { y: 50, opacity: 0, transition: { y: { stiffness: 1000 } } }
};


const mobileMenuItems = [
    { text: 'Home', path: '/' },
    { text: 'Why Shop With Us', path: '/why-us' },
    { text: 'FAQ', path: '/faq' },
    { text: 'Shop', path: '/browse' },
    { text: 'About Us', path: '/about' },
    { text: 'Contact Us', path: '/care' }
];

function MobileNavPage({ isLoggedIn, cartItemCount }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileAccountOpen, setMobileAccountOpen] = useState(false);
    const [loadingPath, setLoadingPath] = useState(null);

    // Determine if current user is the parent account
    const isParentAccount = isLoggedIn && localStorage.getItem('isParent') === 'true';
    const canAccessParentDashboard = isLoggedIn && (localStorage.getItem('isParent') === 'true' || localStorage.getItem('switchedByParent') === 'true');

    // Prevent background scrolling when this page is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    const handleMobileNav = (path) => {
        if (path === '/catalog') {
            setLoadingPath(path);
            setTimeout(() => {
                navigate(path);
            }, 10);
        } else {
            navigate(path, path === '/' ? { state: { intentional: true } } : undefined);
        }
    };

    const handleMobileLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('contactPerson'); // ensure user name is also cleared
        window.dispatchEvent(new Event('storage')); // notify other tabs
        navigate('/');
    };

    return (
        <motion.div
            variants={mobileNavVariants}
            initial="hidden"
            animate="visible"
            exit="hidden" // This triggers the 'hidden' variant animation on component exit
            style={{
                position: 'fixed',
                top: '80px',
                left: 0,
                width: '100vw',
                backgroundColor: 'white',
                zIndex: 1300,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 80px)',
            }}
        >
            <List sx={{ width: '85%', maxWidth: '380px', pt: 15, pb: 0 }}>
                {mobileMenuItems
                    .filter(item => !(isParentAccount && item.text === 'Shop'))
                    .map((item) => (
                    <motion.div key={item.text} variants={itemVariants}>
                        <ListItem disablePadding>
                            {/* START OF EDIT: Added sx prop for :active state */}
                            <ListItemButton
                                onClick={() => handleMobileNav(item.path)}
                                disabled={loadingPath === item.path}
                                sx={{
                                    textAlign: 'center', py: 1.5,
                                    '&:active .MuiListItemText-primary': {
                                        color: '#75a84b'
                                    }
                                }}
                            >
                                {/* END OF EDIT */}
                                {loadingPath === item.path ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                                        <CircularProgress size={24} sx={{ mr: 2, color: '#75a84b' }} />
                                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#75a84b' }}>
                                            Please Wait
                                        </Typography>
                                    </Box>
                                ) : (
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{
                                            fontSize: '1.5rem',
                                            fontWeight: 'bold',
                                            color: location.pathname === item.path ? '#75a84b' : 'text.primary'
                                        }}
                                    />
                                )}
                            </ListItemButton>
                        </ListItem>
                    </motion.div>
                ))}

                {isLoggedIn ? (
                    <motion.div variants={itemVariants}>
                        {/* START OF EDIT: Added sx prop for :active state */}
                        <ListItemButton
                            onClick={() => setMobileAccountOpen(!mobileAccountOpen)}
                            sx={{
                                mt: '1px', justifyContent: 'center', py: 1.5,
                                '&:active .MuiListItemText-primary': {
                                    color: '#75a84b'
                                }
                            }}
                        >
                            {/* END OF EDIT */}
                            <ListItemText primary="Your Account" primaryTypographyProps={{ fontSize: '1.5rem', fontWeight: 'bold', textAlign: 'center' }} />
                            {mobileAccountOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={mobileAccountOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {/* START OF EDIT: Added sx prop for :active state and active page color */}
                                <ListItemButton
                                    onClick={() => handleMobileNav('/profile')}
                                    sx={{
                                        justifyContent: 'center',
                                        '&:active .MuiListItemText-primary': {
                                            color: '#75a84b'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary="My Profile"
                                        primaryTypographyProps={{
                                            textAlign: 'center',
                                            color: location.pathname === '/profile' ? '#75a84b' : 'text.primary'
                                        }}
                                    />
                                </ListItemButton>
                                {canAccessParentDashboard && (
                                <ListItemButton
                                    onClick={() => handleMobileNav('/parent-dashboard')}
                                    sx={{
                                        justifyContent: 'center',
                                        '&:active .MuiListItemText-primary': {
                                            color: '#75a84b'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary="Parent Dashboard"
                                        primaryTypographyProps={{
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            color: location.pathname === '/parent-dashboard' ? '#75a84b' : 'text.primary'
                                        }}
                                    />
                                </ListItemButton>
                                )}
                                {!isParentAccount && (
                                <ListItemButton
                                    onClick={() => handleMobileNav('/order')}
                                    sx={{
                                        justifyContent: 'center',
                                        '&:active .MuiListItemText-primary': {
                                            color: '#75a84b'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary="Your Orders"
                                        primaryTypographyProps={{
                                            textAlign: 'center',
                                            color: location.pathname === '/order' ? '#75a84b' : 'text.primary'
                                        }}
                                    />
                                </ListItemButton>
                                )}
                                <ListItemButton
                                    onClick={() => handleMobileNav('/statements')}
                                    sx={{
                                        justifyContent: 'center',
                                        '&:active .MuiListItemText-primary': {
                                            color: '#75a84b'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary="Your Statements"
                                        primaryTypographyProps={{
                                            textAlign: 'center',
                                            color: location.pathname === '/statements' ? '#75a84b' : 'text.primary'
                                        }}
                                    />
                                </ListItemButton>
                                {!isParentAccount && (
                                <ListItemButton
                                    onClick={() => handleMobileNav('/cart')}
                                    sx={{
                                        justifyContent: 'center',
                                        '&:active .MuiListItemText-primary': {
                                            color: '#75a84b'
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={`Your Cart${cartItemCount > 0 ? ` (${cartItemCount})` : ''}`}
                                        primaryTypographyProps={{
                                            textAlign: 'center',
                                            color: location.pathname === '/cart' ? '#75a84b' : 'text.primary'
                                        }}
                                    />
                                </ListItemButton>
                                )}
                                {/* END OF EDIT */}
                            </List>
                        </Collapse>
                    </motion.div>
                ) : (
                    <motion.div variants={itemVariants}>
                        <ListItem disablePadding>
                            {/* START OF EDIT: Added sx prop for :active state and active page color */}
                            <ListItemButton
                                onClick={() => handleMobileNav('/login')}
                                sx={{
                                    textAlign: 'center', py: 1.5,
                                    '&:active .MuiListItemText-primary': {
                                        color: '#75a84b'
                                    }
                                }}
                            >
                                <ListItemText
                                    primary="Sign In"
                                    primaryTypographyProps={{
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                        color: location.pathname === '/login' ? '#75a84b' : 'text.primary'
                                    }}
                                />
                            </ListItemButton>
                            {/* END OF EDIT */}
                        </ListItem>
                    </motion.div>
                )}

                {isLoggedIn && (
                    <motion.div variants={itemVariants}>
                        <ListItem disablePadding sx={{ mt: 4 }}>
                            <Button
                                fullWidth
                                onClick={handleMobileLogout}
                                variant="contained"
                                sx={{
                                    bgcolor: '#75a84b', color: 'white', borderRadius: '50px', py: 1.5,
                                    fontWeight: 'bold', '&:hover': { bgcolor: '#7a8a55' }
                                }}
                            >
                                Log Out
                            </Button>
                        </ListItem>
                    </motion.div>
                )}
            </List>
        </motion.div>
    );
}

export default MobileNavPage;
